import { useCallback, useEffect, useState } from "react";
import { useLatest } from "hooks/useLatest";
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest";
import { defaultMergeItems } from "./utils";
import type { UseInfiniteLocalPaginatedRequestArgs } from "./types";

export const useInfiniteLocalPaginatedRequest = <TItem>({
  defaultPage,
  defaultLimit,
  resetDeps,
  service,
  accumulate,
}: UseInfiniteLocalPaginatedRequestArgs<TItem>) => {
  const mergeStrategyRef = useLatest({
    merge: accumulate?.merge ?? defaultMergeItems<TItem>,
  });

  const [items, setItems] = useState<TItem[]>([]);

  const {
    data: requestData,
    error: requestError,
    isLoading: requestIsLoading,
    page,
    nextPage,
    goToPage,
    canNext,
    refresh,
  } = useLocalPaginatedRequest<TItem>({
    defaultPage,
    defaultLimit,
    resetDeps,
    service,
  });

  const canLoadMore = !requestIsLoading && (Boolean(requestError) || canNext);

  const onLoadMore = useCallback(() => {
    if (requestIsLoading || !canNext) return;

    if (requestError) {
      refresh();
      return;
    }

    nextPage();
  }, [requestIsLoading, canNext, requestError, refresh, nextPage]);

  const resetList = useCallback(() => {
    if (page === 1) {
      refresh();
      return;
    }
    goToPage(1);
  }, [page, refresh, goToPage]);

  useEffect(() => {
    if (!requestData || requestIsLoading) return;

    const nextItems = requestData.items ?? [];
    const responsePage = requestData.pagination?.page ?? 1;

    setItems((prev) => {
      if (responsePage <= 1) return nextItems;
      return mergeStrategyRef.current.merge(prev, nextItems);
    });
  }, [requestData, requestIsLoading, mergeStrategyRef]);

  return {
    error: requestError,
    isLoading: requestIsLoading,
    items,
    setItems,
    canLoadMore,
    onLoadMore,
    resetList,
  };
};
